import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import axios from "axios";

import { API } from "./apiConfig";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (data.payment_status === "paid") {
          // Refresh auth context so user object has updated subscription
          await checkAuth();
          setStatus("success");
        } else if (data.status === "expired") {
          setStatus("error");
        } else if (attempts < 5) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [sessionId, checkAuth]);

  const handleGoToTool = () => {
    navigate("/tool");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="text-center max-w-sm">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="text-[#0A1F3B] animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-[#0A1F3B] mb-2">Processing payment...</h1>
            <p className="text-slate-600 text-sm">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-[#0A1F3B] mb-2">Payment successful!</h1>
            <p className="text-slate-600 text-sm mb-6">Your account has been upgraded. Start processing payrolls.</p>
            <button
              onClick={handleGoToTool}
              className="inline-flex items-center gap-2 bg-[#0A1F3B] text-white font-bold rounded-lg px-6 py-3 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(10,31,59,0.3)] transition-all duration-300"
              data-testid="go-to-tool-btn"
            >
              Open Payroll Tool
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-[#0A1F3B] mb-2">
              {status === "timeout" ? "Payment verification timed out" : "Something went wrong"}
            </h1>
            <p className="text-slate-600 text-sm mb-6">
              If you were charged, your account will be updated shortly. Contact support if the issue persists.
            </p>
            <Link
              to="/tool"
              className="inline-flex items-center gap-2 bg-[#0A1F3B] text-[#0A1F3B] font-semibold rounded-lg px-6 py-3 hover:bg-[#0A1F3B]/80 transition-colors"
            >
              Back to Tool
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
